/* Редактор списка коротких значений: этапы воронки, стадии аккаунтинга, направления.
   Строка правится на месте, порядок меняется перетаскиванием за ручку,
   удаление — свайпом влево, как в списках iOS. */

import { List, ListItem } from "konsta/react";
import React, { useRef, useState } from "react";

import { Ion } from "./icons.jsx";
import Palette from "./Palette.jsx";
import SwipeRow from "./SwipeRow.jsx";
import { haptic } from "../lib/tg.js";

export default function ItemsEditor({
  caption,
  placeholder,
  items,
  onChange,
  /** Порядок несёт смысл: показываем номера и ручку перетаскивания */
  ordered = false,
  /** Карта «значение → цвет». Задана — у строк появляется квадратик выбора цвета */
  colors,
  onColorsChange,
}) {
  const rowsRef = useRef([]);
  const drag = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [picking, setPicking] = useState(null);

  /* Цвет привязан к тексту этапа, поэтому при переименовании переносим его на новое имя */
  const edit = (i) => (e) => {
    const next = e.target.value;
    const was = items[i];
    onChange(items.map((v, n) => (n === i ? next : v)));
    if (colors && was && colors[was] !== undefined && was !== next) {
      const { [was]: color, ...rest } = colors;
      onColorsChange({ ...rest, ...(next ? { [next]: color } : {}) });
    }
  };

  const remove = (i) => {
    haptic.light();
    const was = items[i];
    onChange(items.filter((_, n) => n !== i));
    if (colors && was && colors[was] !== undefined) {
      const { [was]: _drop, ...rest } = colors;
      onColorsChange(rest);
    }
  };

  const add = () => { haptic.light(); onChange([...items, ""]); };

  const setColor = (name, color) => {
    haptic.select();
    setPicking(null);
    if (!name) return;
    if (!color) {
      const { [name]: _drop, ...rest } = colors;
      onColorsChange(rest);
      return;
    }
    onColorsChange({ ...colors, [name]: color });
  };

  /* ── Перетаскивание ──
     Список переставляется прямо под пальцем, поэтому позиция ищется
     по текущим прямоугольникам строк, а не по стартовым координатам. */
  const onHandleDown = (i) => (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { index: i };
    setDragging(i);
    haptic.select();
  };

  const onHandleMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const y = e.clientY;
    let target = d.index;
    rowsRef.current.forEach((el, n) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (y >= r.top && y <= r.bottom) target = n;
    });
    if (target === d.index) return;
    const next = [...items];
    const [moved] = next.splice(d.index, 1);
    next.splice(target, 0, moved);
    d.index = target;
    setDragging(target);
    haptic.select();
    onChange(next);
  };

  const onHandleUp = () => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(null);
  };

  return (
    <div className="mt-8">
      <div className="px-8 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        {caption}
      </div>
      <List strong inset className="!my-0">
        {items.map((v, i) => (
          <React.Fragment key={i}>
            <Row
              ref={(el) => { rowsRef.current[i] = el; }}
              index={i}
              value={v}
              placeholder={placeholder}
              ordered={ordered}
              dragging={dragging === i}
              color={colors ? colors[v] : undefined}
              onPickColor={colors ? () => { haptic.light(); setPicking(picking === i ? null : i); } : null}
              onEdit={edit(i)}
              onRemove={() => remove(i)}
              onHandleDown={onHandleDown(i)}
              onHandleMove={onHandleMove}
              onHandleUp={onHandleUp}
            />
            {picking === i ? (
              <Palette value={colors[v]} onPick={(c) => setColor(v, c)} />
            ) : null}
          </React.Fragment>
        ))}
        <ListItem
          link
          chevron={false}
          colors={{ primaryTextIos: "text-[color:var(--tg-link)]" }}
          media={<Ion.add size={20} style={{ color: "var(--tg-link)" }} />}
          title="Добавить"
          onClick={add}
        />
      </List>
    </div>
  );
}

/* Строка списка: содержимое, ручка перетаскивания и свайп для удаления */
function Row({
  ref, index, value, placeholder, ordered, dragging, color, onPickColor,
  onEdit, onRemove, onHandleDown, onHandleMove, onHandleUp,
}) {
  return (
    <SwipeRow
      ref={ref}
      onDelete={onRemove}
      className="hairline-b"
      contentClassName="flex items-center gap-3 ps-4 pe-4 py-3"
      style={{ opacity: dragging ? 0.5 : 1 }}
    >
      {ordered ? (
        <span className="num w-5 shrink-0 text-center text-[13px]" style={{ color: "var(--tg-hint)" }}>
          {index + 1}
        </span>
      ) : null}

      <input
        className="flex-1 min-w-0 bg-transparent outline-none text-[17px]"
        style={{ color: "var(--tg-text)" }}
        placeholder={placeholder}
        value={value}
        onChange={onEdit}
      />

      {/* Квадратик цвета этапа: пустой — цвет считается по позиции в воронке */}
      {onPickColor ? (
        <button
          data-nodrag
          type="button"
          className="shrink-0 w-6 h-6 rounded-md active:opacity-60"
          style={{
            background: color || "transparent",
            border: color ? `1px solid ${color}` : "1px dashed var(--tg-hint)",
          }}
          onClick={onPickColor}
          aria-label="Цвет этапа"
        />
      ) : null}

      {ordered ? (
        <span
          data-nodrag
          className="shrink-0 -me-1 p-1 touch-none cursor-grab"
          style={{ color: "var(--tg-hint)" }}
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
          aria-label="Перетащить"
        >
          <Ion.reorderThree size={22} />
        </span>
      ) : null}
    </SwipeRow>
  );
}
